import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Trash2 } from "lucide-react";
import { api } from "../../../software/api";
import { money } from "./shared";
import { EmailRecipientsModal } from "./EmailRecipientsModal";
import { printLegacyInvoice, ensureCompanyInfo } from "../reports/legacy/docs";
import { useAuth } from "../../../software/AuthContext";

/* Forms_InvoicePaymentHow ("How Is Client Paying?") + the two forms it opens:
   InvoicePaymentsSubform (bank transfer) and POSInvoiceResult2 (POS payment). 1 twip = 1/15 px. */
const AB = { fontFamily: "'Arial Black', Arial, sans-serif" };
const paidOf = (s) => (s.payments || []).reduce((a, p) => a + (p.amount || 0), 0);
const balOf = (s) => Math.round(((s.total || 0) - paidOf(s)) * 100) / 100;
const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "Australia/Sydney" });
const auDate = (d) => (d ? new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString("en-AU") : "");

function Shell({ width, title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid={testid}>
      <div className="mt-10 shadow-2xl border border-slate-500 bg-white" style={{ width, maxWidth: "100%" }}>
        <div className="flex items-center justify-between bg-[#f0f0f0] border-b border-slate-400 px-2 py-1">
          <span className="text-[12px] text-slate-800">{title}</span>
          <button onClick={onClose} data-testid={`${testid}-close`} className="hover:bg-rose-600 hover:text-white px-1 rounded-sm"><X className="h-4 w-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function HowPicker({ sale, onPick, onClose }) {
  const pos = () => {
    if (balOf(sale) === 0) { window.alert("There is no balance amount to be paid. This invoice has been paid in full"); onClose(); return; }
    onPick("pos");
  };
  const blue = "w-full text-[#0000ff] hover:text-[#fff200] bg-[#b4c7e7] leading-none transition-colors";
  return (
    <Shell width={658} title="How Is Client Paying?" onClose={onClose} testid="pay-how">
      <div className="bg-[#000099] px-[26px] pt-[23px] pb-[25px]">
        <button onClick={() => onPick("transfer")} data-testid="pay-how-transfer" style={{ ...AB, height: 94, fontSize: 30 }}
          className="w-full text-[#fff200] border border-[#3a3ab8] hover:bg-[#0a0ab0] leading-tight">DIRECT TRANSFER PAYMENT ONLY (No Cash, No Credit Cards)</button>
        <div className="mt-[15px]">
          <button onClick={pos} data-testid="pay-how-pos" style={{ ...AB, height: 83, fontSize: 60 }} className={blue}>POS PAYMENTS</button>
          <div onClick={pos} data-testid="pay-how-box52" className="bg-[#b4c7e7] cursor-pointer" style={{ height: 7 }} />
          <button onClick={pos} data-testid="pay-how-cards-cash" style={{ ...AB, height: 65, fontSize: 38 }} className={blue}>CREDIT CARDS OR CASH</button>
        </div>
        <button onClick={() => onPick("transfer")} data-testid="pay-how-history" className="block w-full mt-[18px]" style={{ height: 239 }}>
          <img src="/legacy-pay/payment-history-750x562.jpg" alt="Payment History" className="w-full h-full object-contain" />
        </button>
      </div>
    </Shell>
  );
}

function TransferRow({ bal, onSave }) {
  const [d, setD] = useState("");
  const [amt, setAmt] = useState("");
  const amtRef = useRef(null);
  const save = (method) => {
    const a = parseFloat(amt);
    if (!method) return;
    if (!d) { toast.error("Enter the payment date (TODAY)."); return; }
    if (!a) { toast.error("Enter the payment amount."); return; }
    onSave({ date: d, amount: a, method });
  };
  return (
    <div className="grid grid-cols-[54px_115px_1fr_105px_215px_24px] gap-1 items-center px-1 py-1 bg-[#fffbe0]" data-testid="pay-transfer-new">
      <button onClick={() => { setD(today()); amtRef.current?.focus(); }} data-testid="pay-transfer-today" style={AB} className="text-[12px] text-[#0000ff] bg-[#e8e8e8] border border-slate-400 h-[22px]">TODAY</button>
      <input type="date" value={d} onChange={(e) => { setD(e.target.value); if (!amt) setAmt(bal.toFixed(2)); }} data-testid="pay-transfer-date" className="border border-slate-400 h-[22px] text-[12px] px-0.5" />
      <button onClick={() => { setAmt(bal.toFixed(2)); amtRef.current?.focus(); }} data-testid="pay-transfer-full" style={AB} className="text-[12px] text-[#0000ff] bg-[#e8e8e8] border border-slate-400 h-[22px]">FULL BALANCE</button>
      <input ref={amtRef} type="number" step="0.01" value={amt} onChange={(e) => setAmt(e.target.value)} data-testid="pay-transfer-amount" className="border border-slate-400 h-[22px] text-[12px] text-right px-1" />
      <MethodSelect onPick={save} />
      <span />
    </div>
  );
}

function MethodSelect({ onPick }) {
  const [opts, setOpts] = useState([]);
  useEffect(() => { api("GET", "/lookups/payment-methods").then(setOpts).catch(() => setOpts(["Direct Transfer"])); }, []);
  return (
    <select value="" onChange={(e) => onPick(e.target.value)} data-testid="pay-transfer-method" className="border border-slate-400 h-[22px] text-[12px] bg-white">
      <option value="">— Method —</option>
      {opts.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function BankTransfer({ sale, client, onDone, onClose }) {
  const bal = balOf(sale);
  const save = async (p) => {
    try { await api("POST", `/sales/${sale.id}/payments`, { ...p, channel: "transfer" }); toast.success("Payment recorded"); onDone(); }
    catch (e) { toast.error(e.message); }
  };
  const del = async (p) => {
    if (!window.confirm(`Delete this ${money(p.amount)} payment?`)) return;
    try { await api("DELETE", `/sales/${sale.id}/payments/${p.id}`); toast.success("Payment deleted"); onDone(true); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <Shell width={676} title="Invoice Payments" onClose={onClose} testid="pay-transfer">
      <div className="bg-[#404040] text-white px-[38px] pt-2 pb-1">
        <div style={{ ...AB, fontSize: 40 }} className="text-center leading-[1.1] text-white" data-testid="pay-transfer-banner">THIS PAYMENT METHOD IS ONLY FOR BANK TRANSFERS</div>
        <div style={{ fontSize: 30 }} className="text-center font-bold mt-3 truncate" data-testid="pay-transfer-invoiceto">{sale.addressed_to || client?.name || sale.client_name}</div>
        <div className="grid grid-cols-[208px_170px_1fr] items-center mt-2 gap-y-1">
          <span style={{ fontSize: 24 }} className="font-bold">Invoice Amount:</span>
          <span style={{ fontSize: 24 }} className="font-bold text-right" data-testid="pay-transfer-nett">{money(sale.total)}</span>
          <span className="text-[12px] text-right text-slate-300">Client {client?.legacy_id || ""}</span>
          <span style={{ fontSize: 24 }} className="font-bold">Balance Owing:</span>
          <span style={{ fontSize: 24 }} className="font-bold text-right" data-testid="pay-transfer-balance">{money(bal)}</span>
          <span className="text-[12px] text-right text-slate-300">Invoice {sale.sale_number}</span>
        </div>
        <div className="grid grid-cols-[54px_115px_1fr_105px_215px_24px] gap-1 mt-3 text-[18px]" style={AB}>
          <span /><span>Date</span><span /><span>Amount</span><span>Method</span><span />
        </div>
      </div>
      <div className="max-h-[50vh] overflow-y-auto divide-y divide-slate-200" data-testid="pay-transfer-list">
        {(sale.payments || []).map((p, i) => (
          <div key={p.id || i} className="grid grid-cols-[54px_115px_1fr_105px_215px_24px] gap-1 items-center px-1 py-1 text-[12px]" data-testid="pay-transfer-row">
            <span /><span>{auDate(p.date || p.at)}</span><span className="text-slate-400 truncate">{p.taken_by || ""}</span>
            <span className="text-right font-bold">{money(p.amount)}</span><span className="capitalize">{p.method}</span>
            {p.id ? <button onClick={() => del(p)} data-testid="pay-transfer-delete" title="Delete payment"><Trash2 className="h-4 w-4 text-slate-400 hover:text-rose-600" /></button> : <span />}
          </div>
        ))}
        <TransferRow bal={bal} onSave={save} />
      </div>
    </Shell>
  );
}

const CARDS = [["Cash", "1_Cash.png"], ["Eftpos", "1_EftPos.png"], ["Visa", "1200px-Visa_svg.png"], ["Mastercard", "it_photo_101245.jpg"], [null, null], ["Amex", "American-Express-copy.png"]];

function PosPayment({ sale, client, clerk, onDone, onClose }) {
  const bal = balOf(sale);
  const [amt, setAmt] = useState(bal.toFixed(2));
  const [busy, setBusy] = useState(false);
  const take = async (method) => {
    const a = parseFloat(amt);
    if (!a) { toast.error("Enter the amount the client is paying."); return; }
    setBusy(true);
    try { const s = await api("POST", `/sales/${sale.id}/payments`, { method, amount: a, channel: "pos", clerk: clerk || "" }); toast.success(`${method} payment ${money(a)} recorded`); onDone(s); }
    catch (e) { toast.error(e.message); setBusy(false); }
  };
  return (
    <Shell width={861} title="POS Invoice Payment" onClose={onClose} testid="pay-pos">
      <div className="bg-[#84a1c6] text-white text-center py-1" style={{ ...AB, fontSize: 24 }}>
        <div className="truncate" data-testid="pay-pos-invoiceto">Invoice To: {sale.addressed_to || client?.name || sale.client_name}</div>
        <div className="truncate" data-testid="pay-pos-client">Client: {client?.name || sale.client_name}</div>
      </div>
      <div className="bg-white px-1 pt-2 pb-6">
        <div className="grid grid-cols-[227px_185px_1fr_227px_185px] items-center font-bold" style={{ fontSize: 26 }}>
          <span className="text-right">Invoice Amount</span><span className="text-center" data-testid="pay-pos-nett">{money(sale.total)}</span><span />
          <span className="text-right text-[#800000]">Balance Owing</span><span className="text-center text-[#800000]" data-testid="pay-pos-balance">{money(bal)}</span>
        </div>
        <div className="grid grid-cols-[389px_1fr] gap-[34px] mt-[22px] pl-[26px]">
          <div className="bg-[#000099] text-white text-center px-3" style={{ height: 363 }}>
            <div className="font-bold mt-[38px] leading-tight" style={{ fontSize: 24 }}>IS THIS THE AMOUNT THE CLIENT IS WANTING TO PAY</div>
            <input type="number" step="0.01" value={amt} onChange={(e) => setAmt(e.target.value)} autoFocus data-testid="pay-pos-amount"
              style={{ ...AB, fontSize: 24 }} className="mt-[30px] w-[226px] h-[38px] text-center text-black bg-white border border-slate-400" />
            <div className="mt-[26px] leading-snug" style={{ fontSize: 18 }}>If the answer is "YES" then simply select a "Payment" Method on the right...</div>
          </div>
          <div className="grid grid-cols-[188px_188px] gap-x-[12px] gap-y-[16px] content-start pt-[19px]">
            {CARDS.map(([m, img], i) => m ? (
              <button key={m} disabled={busy} onClick={() => take(m)} data-testid={`pay-pos-${m.toLowerCase()}`} title={m}
                className="h-[91px] border border-slate-300 bg-white hover:ring-4 hover:ring-[#fff200] disabled:opacity-50">
                <img src={`/legacy-pay/${img}`} alt={m} className="w-full h-full object-contain" />
              </button>
            ) : <span key={i} />)}
          </div>
        </div>
      </div>
    </Shell>
  );
}

// After a counter / phone card payment: offer the A4 payment receipt by email or print.
function ReceiptPrompt({ sale, client: given, onClose }) {
  const [client, setClient] = useState(given || null);
  const [email, setEmail] = useState(false);
  const { user } = useAuth() || {};
  useEffect(() => { ensureCompanyInfo(); if (!given && sale.client_id) api("GET", `/clients/${sale.client_id}`).then(setClient).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const printA4 = async () => {
    const wo = sale.wo_id ? await api("GET", `/workorders/${sale.wo_id}`).catch(() => null) : null;
    printLegacyInvoice(sale, client, sale.brand, wo); onClose();
  };
  if (email && client) return <EmailRecipientsModal client={client} kind="receipt" doc={sale} brand={sale.brand} employee={user?.name || ""}
    attach={() => printLegacyInvoice(sale, client, sale.brand, null)} onClose={onClose} />;
  const btn = "w-full py-3 font-black text-[17px] rounded border-2 transition-colors";
  return (
    <Shell width={520} title="Payment Receipt" onClose={onClose} testid="pay-receipt">
      <div className="bg-[#000099] p-6 space-y-3 text-center">
        <div style={{ ...AB, fontSize: 22 }} className="text-white">PAYMENT RECORDED</div>
        <div className="text-white text-[15px]" data-testid="pay-receipt-balance">Invoice {sale.sale_number} · Balance Owing {money(balOf(sale))}</div>
        <button onClick={() => setEmail(true)} disabled={!client} data-testid="pay-receipt-email" className={`${btn} border-[#b45309] bg-gradient-to-b from-[#fbbf24] to-[#f59e0b] text-[#4a2500] hover:from-[#fcd34d] disabled:opacity-50`}>EMAIL PAYMENT RECEIPT</button>
        <button onClick={printA4} data-testid="pay-receipt-print" className={`${btn} border-white bg-[#b4c7e7] text-[#0000ff] hover:text-[#000099]`}>PRINT A4 RECEIPT</button>
        <button onClick={onClose} data-testid="pay-receipt-skip" className="text-white/80 hover:text-white underline text-[14px] font-bold">No thanks</button>
      </div>
    </Shell>
  );
}

export function InvoicePaymentHow({ sale: initial, client, reload, onClose, start = "how", clerk }) {
  const [sale, setSale] = useState(initial);
  const [step, setStep] = useState(start);
  const refresh = async () => { const s = await api("GET", `/sales/${sale.id}`); setSale(s); return s; };
  const done = async (stay) => { reload && reload(); if (stay) await refresh(); else onClose(); };
  if (step === "transfer") return <BankTransfer sale={sale} client={client} onDone={done} onClose={onClose} />;
  if (step === "pos") return <PosPayment sale={sale} client={client} clerk={clerk} onDone={(s) => { reload && reload(); setSale(s); setStep("receipt"); }} onClose={onClose} />;
  if (step === "receipt") return <ReceiptPrompt sale={sale} client={client} onClose={onClose} />;
  return <HowPicker sale={sale} onPick={setStep} onClose={onClose} />;
}
